/**
 * Контракт аватара профиля — **единственная точка правды** для полей,
 * которыми клиент обменивается с бэкендом по загрузке и чтению аватара
 * пользователя. Если backend-разработчик поменяет имя поля или схему
 * ответа — правим **здесь** (типы + `buildAvatarFormData`), и остальной
 * код (slice, селекторы, компонент) поедет с новым контрактом
 * автоматически.
 *
 * Соответствующий эндпоинт — в `src/api/apiConfig.ts`:
 *   profileAvatar → POST /api/profile/avatar
 *
 * === Контракт запроса: POST /api/profile/avatar ===
 *
 * Request: `multipart/form-data`
 *
 * ```
 * file: File (image/png | image/jpeg | image/webp), <= 5 MB
 * ```
 *
 * Имя поля формы (`file`) фиксировано — см. `AVATAR_FIELD_NAME` ниже и
 * `buildAvatarFormData`. Если backend использует другое имя — меняем
 * константу только здесь.
 *
 * Success:  200 OK
 * ```json
 * { "url": "https://cdn.example.com/avatars/u42.jpg" }
 * ```
 *
 * Errors:
 *   - 400 — `{ detail: "<ключ validation.*>" }`
 *   - 413 — файл слишком большой
 *   - 415 — недопустимый формат файла
 *   - 401 — требует авторизацию
 *
 * === Контракт ответа: GET /api/profile/avatar (для первичной загрузки) ===
 *
 * ```json
 * { "url": "https://cdn.example.com/avatars/u42.jpg" | null }
 * ```
 *
 * `null` означает «аватар не загружен», UI показывает иконку-фоллбек.
 *
 * === Контракт запроса: DELETE /api/profile/avatar ===
 *
 * Request: тело отсутствует.
 * Success: 204 No Content — сервер сам сбрасывает аватар пользователя в
 * `null`, клиент после успеха снова показывает иконку-фоллбек.
 *
 * Errors:
 *   - 401 — требует авторизацию
 *   - 404 — у пользователя не было аватара (UI может игнорировать и всё
 *     равно считать операцию успешной, т.к. цель уже достигнута).
 */

/**
 * Слайс-состояние аватара. `url` хранит либо боевой URL с CDN (после
 * успешного аплоада / GET), либо `blob:`-URL для оптимистичного превью,
 * пока идёт загрузка на сервер.
 *
 * `isUploading` и `isDeleting` держат одинаковый «сейчас идёт
 * запрос»-смысл, но разведены, чтобы UI мог отдельно различать
 * прогресс-состояние (disabled + спиннер) и семантику кнопки: обе они
 * собираются в один флаг `selectProfileAvatarIsBusy`.
 */
export interface ProfileAvatarState {
	url: string | null;
	avatarId: number | null;
	isUploading: boolean;
	isDeleting: boolean;
	successMessage: string | null;
	error: string | null;
}

/**
 * Форма ответа сервера на POST-загрузку аватара.
 * Используется при замене `simulateUploadAvatar` на реальный `apiUrl.post`.
 */
export interface AvatarUploadResponse {
	url: string;
}

/**
 * Имя поля `multipart/form-data` в запросе на загрузку.
 * Фиксировано на стороне контракта — backend читает ровно это имя.
 */
export const AVATAR_FIELD_NAME = 'file';

/**
 * UI (`File`) → payload для API. Единственное место, где собирается
 * `FormData` для аплоада. Backend меняет имя поля — правим тут.
 */
export const buildAvatarFormData = (file: File): FormData => {
	const fd = new FormData();
	fd.append(AVATAR_FIELD_NAME, file);
	return fd;
};
